import { useEffect, useState } from "react";
import { Trash2, X } from "lucide-react";
import { Button, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { UserPicker } from "@/components/users/UserPicker";
import { TaskAssigneeAvatar } from "./TaskAssigneeAvatar";
import { TaskTagChips } from "./TaskTagChips";
import { TaskRelatedEntityLink } from "./TaskRelatedEntityLink";
import { TaskActivityLogPanel } from "./TaskActivityLogPanel";
import {
  useChangeWorkTaskStatus,
  useDeleteWorkTask,
  useReassignWorkTask,
  useRestoreWorkTask,
  useSetWorkTaskParticipants,
  useSetWorkTaskProgress,
  useTaskTags,
  useUpdateWorkTask,
} from "../hooks/useWorkTasks";
import type {
  WorkTaskDto,
  WorkTaskPriority,
  WorkTaskStatus,
} from "@/api/workTasks";
import { ApiError } from "@/api/client";
import { calendarDateInputToIso, useDateFormatter } from "@/utils/datetime";

interface Props {
  task: WorkTaskDto;
  onDeleted: () => void;
}

const PRIORITIES: WorkTaskPriority[] = ["Low", "Medium", "High", "Critical"];

const STATUSES: { value: WorkTaskStatus; label: string }[] = [
  { value: "Open", label: "Open" },
  { value: "InProgress", label: "In progress" },
  { value: "Done", label: "Done" },
];

export function TaskMetadataSidebar({ task, onDeleted }: Props) {
  const { user, hasPermission } = useAuth();
  const isAdmin = hasPermission("Tasks.ViewAll");
  const canAssignToOthers = hasPermission("Tasks.AssignToOthers");
  const canDelete = task.createdBy.userId === user?.id || isAdmin;
  const canRestore = isAdmin;
  // Tasks Module v2 — participant management: creator + owner (assignee) + admin.
  const ownerId = task.assignee?.userId ?? task.createdBy.userId;
  const canManageParticipants =
    task.createdBy.userId === user?.id || task.assignee?.userId === user?.id || isAdmin;

  const tagsQ = useTaskTags();
  const update = useUpdateWorkTask(task.id);
  const changeStatus = useChangeWorkTaskStatus(task.id);
  const reassign = useReassignWorkTask(task.id);
  const remove = useDeleteWorkTask();
  const restore = useRestoreWorkTask();
  const setProgress = useSetWorkTaskProgress(task.id);
  const saveParticipants = useSetWorkTaskParticipants(task.id);
  const { toast } = useToast();
  const { confirm } = useDialogs();
  const { dateTime } = useDateFormatter();

  const [reassignOpen, setReassignOpen] = useState(false);
  // The picker carries the selected user id and a *display-only* name.
  // The raw user id is never rendered to the user; the v1.1 GUID-leak
  // fix from § 3.4 depends on this state shape.
  const [assigneeDraftId, setAssigneeDraftId] = useState<string | null>(task.assignee?.userId ?? null);
  const [tagsOpen, setTagsOpen] = useState(false);

  // Progress slider — local draft for smooth dragging; commit on release.
  const [progressDraft, setProgressDraft] = useState(task.progressPercent);
  useEffect(() => setProgressDraft(task.progressPercent), [task.progressPercent]);

  // Participant list — optimistic local mirror so rapid add/remove stays
  // correct (each edit sends the full desired set). Re-synced from the
  // server whenever the actual participant set changes.
  const [participants, setParticipants] = useState(task.participants);
  const participantsKey = task.participants.map((p) => p.userId).join(",");
  useEffect(() => setParticipants(task.participants), [participantsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [participantPickerKey, setParticipantPickerKey] = useState(0);

  const handleErr = (err: unknown, fallback: string) => {
    const msg = err instanceof ApiError ? err.errors?.[0] ?? err.message : fallback;
    toast({ message: msg, tone: "error" });
  };

  const commitProgress = (percent: number) => {
    if (percent === task.progressPercent) return;
    setProgress.mutateAsync(percent).catch((e) => handleErr(e, "Could not update progress."));
  };

  const commitParticipants = (next: { userId: string; displayName: string }[]) => {
    setParticipants(next); // optimistic
    saveParticipants.mutateAsync(next.map((p) => p.userId)).catch((e) => {
      setParticipants(task.participants); // roll back
      handleErr(e, "Could not update participants.");
    });
  };

  const addParticipant = (id: string | null, displayName: string) => {
    setParticipantPickerKey((k) => k + 1); // remount the picker empty
    if (!id || id === ownerId || participants.some((p) => p.userId === id)) return;
    commitParticipants([...participants, { userId: id, displayName }]);
  };

  const removeParticipant = (id: string) =>
    commitParticipants(participants.filter((p) => p.userId !== id));

  const setPriority = (next: WorkTaskPriority) =>
    update
      .mutateAsync({ priority: next })
      .catch((e) => handleErr(e, "Could not update priority."));

  const setStatus = (next: WorkTaskStatus) =>
    changeStatus
      .mutateAsync(next)
      .catch((e) => handleErr(e, "Could not update status."));

  const setDue = (iso: string) =>
    update
      .mutateAsync(iso ? { dueDate: calendarDateInputToIso(iso)! } : { clearDueDate: true })
      .catch((e) => handleErr(e, "Could not update due date."));

  const toggleTag = (tagId: string) => {
    const current = task.tags.map((t) => t.id);
    const next = current.includes(tagId)
      ? current.filter((x) => x !== tagId)
      : [...current, tagId];
    update.mutateAsync({ tagIds: next }).catch((e) => handleErr(e, "Could not update tags."));
  };

  return (
    <aside className="flex flex-col gap-space-4">
      <Section label="Assignee">
        {reassignOpen ? (
          <div className="flex flex-col gap-space-2">
            <UserPicker
              value={assigneeDraftId}
              initialDisplayName={task.assignee?.displayName ?? ""}
              autoFocus
              allowUnassigned
              ariaLabel="Assignee"
              placeholder="Search by name or email"
              onChange={(id) => setAssigneeDraftId(id)}
            />
            <div className="flex justify-end gap-space-2">
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={() => {
                  setAssigneeDraftId(task.assignee?.userId ?? null);
                  setReassignOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                type="button"
                disabled={
                  !canAssignToOthers && assigneeDraftId !== null && assigneeDraftId !== user?.id
                }
                onClick={async () => {
                  try {
                    await reassign.mutateAsync(assigneeDraftId);
                    setReassignOpen(false);
                  } catch (e) {
                    handleErr(e, "Could not reassign.");
                  }
                }}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-space-2">
            <TaskAssigneeAvatar assignee={task.assignee} />
            <Button
              variant="tertiary"
              size="sm"
              type="button"
              onClick={() => {
                setAssigneeDraftId(task.assignee?.userId ?? null);
                setReassignOpen(true);
              }}
            >
              Change
            </Button>
          </div>
        )}
      </Section>

      <Section label="Created by">
        <div className="text-small text-onyx">
          {task.createdBy.displayName}
          <div className="text-label text-gray-mid">
            {dateTime(task.createdAt)}
          </div>
        </div>
      </Section>

      <Section label="Status">
        <Select
          value={task.status}
          onChange={(e) => setStatus(e.target.value as WorkTaskStatus)}
          aria-label="Status"
        >
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
      </Section>

      {task.status === "InProgress" && (
        <Section label="Progress">
          <div className="flex items-center gap-space-3">
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={progressDraft}
              onChange={(e) => setProgressDraft(Number(e.target.value))}
              onPointerUp={() => commitProgress(progressDraft)}
              onKeyUp={() => commitProgress(progressDraft)}
              aria-label="Progress"
              className="flex-1 accent-gold"
            />
            <span className="w-10 text-right text-small tabular-nums text-onyx">
              {progressDraft}%
            </span>
          </div>
        </Section>
      )}

      <Section label="Priority">
        <Select
          value={task.priority}
          onChange={(e) => setPriority(e.target.value as WorkTaskPriority)}
          aria-label="Priority"
        >
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </Select>
      </Section>

      <Section label="Due date">
        <Input
          type="date"
          value={task.dueDate ? task.dueDate.slice(0, 10) : ""}
          onChange={(e) => setDue(e.target.value)}
          aria-label="Due date"
        />
      </Section>

      {canManageParticipants ? (
        <Section label="Participants">
          {participants.length === 0 && (
            <p className="text-label text-gray-mid">
              Not shared. Add people to make this a team task.
            </p>
          )}
          {participants.length > 0 && (
            <div className="flex flex-wrap gap-space-2">
              {participants.map((p) => (
                <span
                  key={p.userId}
                  className="inline-flex items-center gap-space-1 rounded-sm bg-white-warm border border-border-light px-space-2 py-[3px] text-small text-onyx"
                >
                  {p.displayName}
                  <button
                    type="button"
                    onClick={() => removeParticipant(p.userId)}
                    aria-label={`Remove ${p.displayName}`}
                    className="text-gray-mid hover:text-onyx"
                  >
                    <X size={12} aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          )}
          <UserPicker
            key={participantPickerKey}
            value={null}
            ariaLabel="Add participant"
            placeholder="Add a participant"
            onChange={addParticipant}
          />
        </Section>
      ) : (
        task.isMultiuser && (
          <Section label="Participants">
            <div className="flex flex-wrap gap-space-2">
              {task.participants.map((p) => (
                <span
                  key={p.userId}
                  className="inline-flex items-center rounded-sm bg-white-warm border border-border-light px-space-2 py-[3px] text-small text-onyx"
                >
                  {p.displayName}
                </span>
              ))}
            </div>
          </Section>
        )
      )}

      <Section label="Tags">
        <TaskTagChips tags={task.tags} />
        <Button
          variant="tertiary"
          size="sm"
          type="button"
          className="self-start mt-space-2"
          onClick={() => setTagsOpen((v) => !v)}
        >
          {tagsOpen ? "Done" : "Edit tags"}
        </Button>
        {tagsOpen && (
          <div className="flex flex-wrap gap-space-2 mt-space-2">
            {(tagsQ.data ?? []).map((t) => {
              const active = task.tags.some((x) => x.id === t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleTag(t.id)}
                  className={
                    "px-space-2 py-[3px] rounded-sm font-display text-[10px] " +
                    "font-bold uppercase tracking-[0.12em] border " +
                    (active
                      ? "bg-gold text-onyx border-gold"
                      : "bg-white-pure text-gray-mid border-border-light hover:bg-white-warm")
                  }
                >
                  {t.displayName}
                </button>
              );
            })}
          </div>
        )}
      </Section>

      {task.relatedEntity && (
        <Section label="Related entity">
          <TaskRelatedEntityLink related={task.relatedEntity} />
        </Section>
      )}

      <TaskActivityLogPanel taskId={task.id} />

      {task.isDeleted && canRestore && (
        <Button
          variant="secondary"
          type="button"
          onClick={() =>
            restore
              .mutateAsync(task.id)
              .catch((e) => handleErr(e, "Could not restore."))
          }
        >
          Restore task
        </Button>
      )}

      {!task.isDeleted && canDelete && (
        <Button
          variant="text"
          size="sm"
          type="button"
          className="self-start text-status-error"
          onClick={async () => {
            const ok = await confirm({
              title: "Delete task?",
              message: "This task will be deleted. This action can be undone by an administrator.",
              confirmLabel: "Delete",
              destructive: true,
            });
            if (!ok) return;
            try {
              await remove.mutateAsync(task.id);
              toast({ message: "Task deleted.", tone: "success" });
              onDeleted();
            } catch (e) {
              handleErr(e, "Could not delete.");
            }
          }}
        >
          <Trash2 size={14} aria-hidden /> Delete task
        </Button>
      )}
    </aside>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-space-2">
      <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
        {label}
      </div>
      {children}
    </div>
  );
}
